import { create } from 'zustand';
import { persist, subscribeWithSelector } from 'zustand/middleware';

const STORAGE_KEY = 'melodiza:ui';

// Custom broadcast middleware: relays state changes across tabs
// Serializable state keys (excludes functions)
const BROADCAST_KEYS = [
  'sidebarOpen',
  'searchOpen',
  'isFullscreen',
  'isQueueOpen',
  'currentPlaylist',
  'showMigrationBanner',
  'startPlayerTour',
];

const broadcast = (config) => (set, get, api) => {
  const channel =
    typeof BroadcastChannel !== 'undefined'
      ? new BroadcastChannel(STORAGE_KEY)
      : null;
  let isReceiving = false;
  if (channel) {
    channel.addEventListener('message', (e) => {
      isReceiving = true;
      api.setState(e.data);
      isReceiving = false;
    });
  }
  return config(
    (...args) => {
      set(...args);
      if (channel && !isReceiving) {
        // Only broadcast serializable state (no functions)
        const full = get();
        const serializable = Object.fromEntries(
          BROADCAST_KEYS.map((k) => [k, full[k]])
        );
        try {
          channel.postMessage(serializable);
        } catch {
          /* DataCloneError: skip */
        }
      }
    },
    get,
    api
  );
};

export const useUIStore = create(
  subscribeWithSelector(
    persist(
      broadcast((set) => ({
        // state
        sidebarOpen: false,
        searchOpen: false,
        isFullscreen: false,
        isQueueOpen: false,
        currentPlaylist: null,
        showMigrationBanner: false,
        startPlayerTour: false,

        // actions (équivalent des callbacks UIContext)
        setSidebarOpen: (v) => set({ sidebarOpen: v }),
        toggleSidebar: () => set((s) => ({ sidebarOpen: !s.sidebarOpen })),
        closeSidebar: () => set({ sidebarOpen: false }),
        setSearchOpen: (v) => set({ searchOpen: v }),
        openSearch: () => set({ searchOpen: true }),
        closeSearch: () => set({ searchOpen: false }),
        setIsFullscreen: (v) => set({ isFullscreen: v }),
        openFullscreen: () => set({ isFullscreen: true }),
        closeFullscreen: () => set({ isFullscreen: false }),
        setIsQueueOpen: (v) => set({ isQueueOpen: v }),
        openQueue: () => set({ isQueueOpen: true }),
        closeQueue: () => set({ isQueueOpen: false }),
        toggleQueue: () => set((s) => ({ isQueueOpen: !s.isQueueOpen })),
        setCurrentPlaylist: (p) => set({ currentPlaylist: p }),
        setShowMigrationBanner: (v) => set({ showMigrationBanner: v }),
        setStartPlayerTour: (v) => set({ startPlayerTour: v }),

        // multi-tenant safety
        resetUIStore: () =>
          set({
            sidebarOpen: false,
            searchOpen: false,
            isFullscreen: false,
            isQueueOpen: false,
            currentPlaylist: null,
            showMigrationBanner: false,
            startPlayerTour: false,
          }),
      })),
      {
        name: STORAGE_KEY,
        // Persist only the stable subset (not open modals)
        partialize: (s) => ({
          currentPlaylist: s.currentPlaylist,
        }),
      }
    )
  )
);
